import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SITES_CHANGED_EVENT,
  logout,
  me,
  setSite,
  sites,
  storedSite,
  type CurrentUser,
  type SiteSummary,
} from './api';
import { makeT, type Locale, type ShellKey } from './i18n';
import { MODULE_ROUTES, visibleRoutes } from './routes';
import { LoginView } from './shell/login';
import { ToastProvider } from './shell/toast';
import { ConfirmProvider } from './shell/confirm';
import { AppFrame } from './shell/frame';
import { useHashLocation } from './shell/hooks';
import { useTheme } from './shell/theme';
import { EmptyState, Select, SplashState } from './shell/kit';

export default function App() {
  const [user, setUser] = useState<CurrentUser | undefined>(undefined);
  const [siteList, setSiteList] = useState<readonly SiteSummary[]>([]);
  const [siteId, setSiteId] = useState<string | null>(storedSite());
  const [ready, setReady] = useState(false);
  const [location, navigate] = useHashLocation();
  const theme = useTheme();

  const locale: Locale = user?.locale ?? 'ru';
  const t = useMemo(() => makeT(locale), [locale]);

  const refresh = useCallback(async (): Promise<void> => {
    // Сессия в HttpOnly-cookie: вошёл ли сотрудник, знает только сервер.
    try {
      const current = await me();
      setUser(current);
      setSiteList(await sites());
    } catch {
      setUser(undefined);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onSitesChanged = (): void => {
      void sites().then(setSiteList).catch(() => undefined);
    };
    window.addEventListener(SITES_CHANGED_EVENT, onSitesChanged);
    return () => window.removeEventListener(SITES_CHANGED_EVENT, onSitesChanged);
  }, []);

  // Меню строится из ответа сервера, а не из догадок о ролях: раньше кассир видел
  // все одиннадцать разделов, из которых работали два, а остальные отвечали 403.
  const allowed = useMemo(
    () => (user ? visibleRoutes(user.permissionsAnySite) : []),
    [user],
  );

  const route =
    allowed.find((r) => r.key === location.module) ?? allowed[0] ?? undefined;

  useEffect(() => {
    if (route && location.module !== route.key) {
      navigate({ module: route.key, tab: null, detail: null });
    }
  }, [route, location.module, navigate]);

  if (!ready) return <SplashState label="Проверяем вход" />;

  if (!user) {
    return <LoginView t={t} locale={locale} onSignedIn={() => void refresh()} />;
  }

  const roles = [...new Set(user.bindings.map((b) => b.role))]
    .map((role) => t(`role.${role}` as ShellKey))
    .join(', ');

  const siteSelect = (
    <Select
      aria-label={t('shell.site.select')}
      value={siteId ?? ''}
      onChange={(e) => {
        const value = e.target.value || null;
        setSiteId(value);
        setSite(value);
        // Права зависят от выбранного объекта — перечитываем их сразу.
        void refresh();
      }}
    >
      <option value="">{t('shell.site.all')}</option>
      {siteList.map((s) => (
        <option key={s.id} value={s.id}>
          {s.code} · {s.name}
        </option>
      ))}
    </Select>
  );

  return (
    <ToastProvider>
      <ConfirmProvider>
        <AppFrame
          brand={t('shell.title')}
          userName={user.displayName ?? user.id}
          userRoles={roles}
          nav={allowed.map((item) => ({
            key: item.key,
            label: locale === 'kk' ? item.titleKk : item.titleRu,
          }))}
          activeKey={route?.key}
          onNavigate={(key) => navigate({ module: key, tab: null, detail: null })}
          navLabel={t('shell.nav')}
          {...(allowed.length < MODULE_ROUTES.length
            ? { navNote: 'Показаны только разделы, доступные вашей роли.' }
            : {})}
          siteSelect={siteSelect}
          theme={theme}
          onLogout={() =>
            void logout().then(() => {
              // Объект живёт в двух местах: в api (заголовок x-moika-site) и
              // здесь, в состоянии шапки. logout() чистит первое; без второго
              // следующий вошедший видел в шапке чужой объект, а запросы уходили
              // без него — с 403 и ложными «ничего нет» (QA.md, Q17).
              setSiteId(null);
              setUser(undefined);
            })
          }
          logoutLabel={t('shell.logout')}
        >
          {route ? (
            <route.Component
              t={t}
              user={user}
              siteId={siteId}
              title={locale === 'kk' ? route.titleKk : route.titleRu}
            />
          ) : (
            <div className="mx-auto w-full max-w-(--w-page) px-4 py-10 sm:px-6 lg:px-8">
              <EmptyState
                title="Для вашей роли нет доступных разделов"
                reason="Права выдаются по объекту и роли. Обратитесь к администратору сети, если раздел нужен для работы."
              />
            </div>
          )}
        </AppFrame>
      </ConfirmProvider>
    </ToastProvider>
  );
}
